import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LoggingService } from "ionic-logging-service";
import * as moment from "moment";
import { BestoGoConfigurationDto } from "../types/ca";
import { CardPaymentServiceNets } from "./cardpayment.service.nets";
import { CardPaymentAndPrintServicePoplatek } from "./cardpayment.service.poplatek";
import { ConfigurationService } from "./configuration";
import { StorageService } from "./storage.service";
import { UtilsService } from "./utils";

@Injectable({
  providedIn: "root"
})
export class ReconciliationService {

  reconciliatedDates: Date[] = [];

  logger;

  constructor(private storage: StorageService, private translate: TranslateService, private utils: UtilsService,
    private loggingService: LoggingService, private conf: ConfigurationService, private poplatek: CardPaymentAndPrintServicePoplatek,
    private nets: CardPaymentServiceNets) {
      this.logger = loggingService.getLogger("BestoGo.ReconciliationService");
    }

  async getDatesFromStorage() {
    const reconciliatedDates = await this.storage.get("reconciliatedDates");
    if (reconciliatedDates) {
      this.reconciliatedDates = reconciliatedDates;
    }
  }

  addDate(newDate: Date) {
    const date = moment(newDate).hour() >= 5 ?
      moment(newDate).startOf("day").add(5, "hours") :
      moment(newDate).startOf("day").subtract(19, "hours");
    this.reconciliatedDates.push(date.toDate());
    this.storage.set("reconciliatedDates", this.reconciliatedDates);
  }

  removeDate(removedDate: Date) {
    const date = moment(removedDate).hour() >= 5 ?
      moment(removedDate).startOf("day").add(5, "hours") :
      moment(removedDate).startOf("day").subtract(19, "hours");
    this.reconciliatedDates = this.reconciliatedDates.filter((d: Date) => !date.isSame(d));
    this.storage.set("reconciliatedDates", this.reconciliatedDates);
  }

  isDateReconciliated(date: moment.Moment): boolean {
    date.startOf("day").add(5, "hours");
    return this.reconciliatedDates.some(
      (d: Date) => date.isSameOrAfter(moment(d)) && date.isBefore(moment(d).add(24, "hours"))
    );
  }

  createReconciliationReceipt(rows, paymentTypes, lineWidth, date, numOfProductsSold: number,
    conf: BestoGoConfigurationDto, restaurant?, clerkId?) {
      const outputs = [];
      let orgHeader1 = "";
      if (window.localStorage.getItem("placeName")) {
        orgHeader1 = window.localStorage.getItem("placeName");
      }
      const priceCoef = 1;
      let totalSum = 0;
      const vats = {};
      if (restaurant?.n) {
        outputs.push(this.utils.middlePad(restaurant?.n, " ", lineWidth));
      }
      if (restaurant?.addr) {
        outputs.push(this.utils.middlePad(restaurant?.addr + ", " + restaurant?.pi + " " + restaurant?.city, " ", lineWidth));
      }
      if (restaurant?.tel) {
        outputs.push(this.utils.middlePad(restaurant?.tel, " ", lineWidth));
      }
      if (restaurant?.www) {
        outputs.push(this.utils.middlePad(restaurant?.www, " ", lineWidth));
      }
      if (orgHeader1.length !== 0) {
        outputs.push(this.utils.middlePad(orgHeader1, " ", lineWidth));
      }
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      outputs.push(this.utils.middlePad(this.translate.instant("Reconciliation")," ", lineWidth));
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      outputs.push(this.utils.leftPad(this.utils.formatDateTime(date), " ", lineWidth));
      if (clerkId) {
        outputs.push(this.utils.leftPad(`${this.translate.instant("Clerk ID")} ${clerkId}`, " ", lineWidth));
      }
      if (conf && conf.crMachineNumber) {
        this.logger.debug("rowsToReconciliationReceipt", "Conf: ", conf);
        outputs.push(this.utils.leftPad(conf.crMachineNumber + " " + conf.appVersion,
          " ", lineWidth));
      }
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      for (const o of rows) {
        let rowSum = parseFloat(o.price) * parseFloat(o.amount);
        if (o.discount) {
          if (o.discount.type === "%") {
            rowSum -= rowSum * o.discount.value / 100;
          } else {
            rowSum -= o.discount.value;
          }
        }
        totalSum += priceCoef * rowSum;
        const rowName = o.parentRowUuid ? "  " + o.name : o.name;
        outputs.push(this.utils.rightPad(o.name, " ", lineWidth - 8) +
          this.utils.leftPad(((priceCoef * parseFloat(o.price)) * parseFloat(o.amount)).toFixed(2), " ", 8));
        if (o.amount > 1) {
          outputs.push(this.utils.rightPad(" ", " ", lineWidth - 8) +
            this.utils.leftPad("" + parseFloat(o.amount) + "x" + (priceCoef * parseFloat(o.price)).toFixed(2), " ", 8));
        }
        if (o.discount) {
          if (o.discount.type === "%") {
            outputs.push(this.utils.rightPad("   " + o.discount.name, " ", lineWidth - 8) +
              this.utils.leftPad("-" + o.discount.type + o.discount.value, " ", 8));
          } else {
            outputs.push(this.utils.rightPad("   " + o.discount.name, " ", lineWidth - 9) +
              this.utils.leftPad("-" + parseFloat(o.discount.value).toFixed(2), " ", 9));
          }
        }
        if (!vats[o.vat]) {
          vats[o.vat] = {vatPercentage: o.vat, totalWithoutVat: 0, totalVat: 0, totalSum: 0};
        }
        vats[o.vat].totalSum += rowSum;
        const vatLessSum = rowSum / (1 + o.vat / 100);
        vats[o.vat].totalWithoutVat += vatLessSum;
        vats[o.vat].totalVat += (rowSum - vatLessSum);
      }
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      outputs.push(this.utils.rightPad(this.translate.instant("Total"), " ", lineWidth - 8) +
        this.utils.leftPad(totalSum.toFixed(2), " ", 8));
      outputs.push(this.utils.rightPad("", " ", lineWidth));
      for (const p of Object.keys(paymentTypes)) {
        outputs.push(this.utils.rightPad(p, " ", lineWidth - 8) +
        this.utils.leftPad(this.utils.formatPrice(paymentTypes[p]), " ", 8));
      }
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      if (lineWidth > 30) {
        outputs.push(this.utils.rightPad(this.translate.instant("Tax%"), " ", lineWidth - 26) +
          this.utils.leftPad(this.translate.instant("Taxless"), " ", 9) +
          this.utils.leftPad(this.translate.instant("Tax"), " ", 7) +
          this.utils.leftPad(this.translate.instant("With tax"), " ", 10));
        for (const vat of Object.keys(vats)) {
          const v = vats[vat];
          outputs.push(this.utils.rightPad(v.vatPercentage + "%", " ", lineWidth - 26) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalWithoutVat)).toFixed(2), " ", 9) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalVat)).toFixed(2), " ", 7) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalSum)).toFixed(2), " ", 10));
        }
      } else {
        outputs.push(this.utils.rightPad(" %", " ", lineWidth - 25) +
          this.utils.leftPad(this.translate.instant("Taxless"), " ", 8) +
          this.utils.leftPad(this.translate.instant("Tax"), " ", 7) +
          this.utils.leftPad(this.translate.instant("With tax"), " ", 10));
        for (const vat of Object.keys(vats)) {
          const v = vats[vat];
          outputs.push(this.utils.rightPad(v.vatPercentage + "%", " ", lineWidth - 24) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalWithoutVat)).toFixed(2), " ", 7) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalVat)).toFixed(2), " ", 7) +
            this.utils.leftPad((priceCoef * parseFloat(v.totalSum)).toFixed(2), " ", 10));
        }
      }
      outputs.push(this.utils.middlePad(" ", " ", lineWidth));
      return outputs;
    }

    endOfDayReconciliation(date: Date) {
      if (this.conf.isPoplatekWsPt()) {
        this.poplatek.endOfDayReconciliation(date);
      } else {
        this.nets.endOfDayReconciliation(date);
      }
    }
}

